/**
 * 动态表单渲染器的**纯逻辑层**
 * -----------------------------------------------------------------------------
 * 渲染与校验规则都放在这里，`FormRenderer.tsx` 只负责把结果画出来 ——
 * 这样做有两个实际好处：
 *   ① 规则可被穷举测试，不必渲染 DOM（本仓未引入 jsdom / testing-library）；
 *   ② 「隐藏字段是否参与必填」「报价怎么算」这类判定只有一份实现，
 *      不会在渲染组件里再抄一遍。
 *
 * 校验一律**复用** `@enroll/shared` 的 `validateAnswers` / `validateFieldValue` /
 * `isFieldVisible`，本文件不新增任何校验规则（口径：判定逻辑三端同一份）。
 */
import {
  type AnswerError,
  FIELD_KEY_PATTERN,
  FieldEditable,
  FieldType,
  type FormFieldDef,
  type FormSchemaView,
  formatCents,
  isFieldVisible,
  validateAnswers,
  validateFieldValue,
} from '@enroll/shared';

export type Answers = Record<string, unknown>;

export interface FormFieldGroupView {
  key: string;
  label: string;
  fields: FormFieldDef[];
}

export interface PriceLine {
  key: string;
  /** 字段名（如「文化程度」） */
  label: string;
  /** 选中项名（如「三级」） */
  optionLabel: string;
  amountCents: number;
}

export interface PriceBreakdown {
  lines: PriceLine[];
  itemsTotalCents: number;
  reductionCents: number;
  /** 应缴额（净值）= `itemsTotalCents − reductionCents`。⚠️ 别再减一次 `reductionCents` */
  payableCents: number;
}

export interface FieldEditing {
  disabled: boolean;
  hint: string | null;
}

/** 该字段此刻是否显示（条件显示消费 `visibleWhen`，仅单条件） */
export function isFieldShown(field: FormFieldDef, answers: Answers): boolean {
  return isFieldVisible(field, answers);
}

/** 当前显示的字段（按 Schema 顺序） */
export function visibleFields(fields: readonly FormFieldDef[], answers: Answers): FormFieldDef[] {
  return fields.filter((field) => isFieldVisible(field, answers));
}

/**
 * 提交 / 保存用的取值：**隐藏字段与未知键一并剔除**。
 *
 * 注意与「局部变量里保留隐藏字段的值」的区别：UI 状态可以留着（用户改回来时不必重填），
 * 但发给服务端的一律是这一份 —— 否则会出现「选了现场领取，地址区块隐藏了，
 * 但请求体里还带着旧地址」这种自相矛盾的载荷。
 */
export function prunedAnswers(fields: readonly FormFieldDef[], answers: Answers): Answers {
  const visible = new Set(visibleFields(fields, answers).map((field) => field.key));
  const out: Answers = {};
  for (const [key, value] of Object.entries(answers)) {
    if (visible.has(key) && FIELD_KEY_PATTERN.test(key)) out[key] = value;
  }
  return out;
}

/**
 * 取表单定义里出现过的字段键（用户可能因为「先填 A 再隐藏 A」而残留取值）。
 * 供页面做「哪些键属于本表单」的判定，不用于剔除隐藏字段。
 */
export function knownFieldKeys(fields: readonly FormFieldDef[]): Set<string> {
  return new Set(fields.map((field) => field.key));
}

/**
 * 只留下「本表单真的存在」的字段错误。
 *
 * `validateAnswers` 会把「答案里有、字段表里没有」的键判成 `表单中不存在该字段`。
 * 这类键的来历是**表单改版**：字段被删掉了，但历史报名单的 `formData` 里仍带着它
 * （服务端保存与提交都不校验未知键，所以只有前端这一步会撞上）。界面上根本没有
 * 对应输入框，学员既改不了也看不懂，只会被一句指不到任何控件的提示卡死。
 *
 * 2026-11-01「营养指导员第一步点下一步提示不存在该字段」即此：该表单 v5 删掉了
 * `salesStaff` / `channelSource` 两个字段，而 14 张老单子的 `formData` 里还留着它们。
 *
 * ⚠️ 这不是「把报错藏起来」：未知键本身就不该出现在答案里，`prunedAnswers` 发的
 * 载荷也不含它们；这里只是不让一句学员无法处理的话挡住流程。
 */
export function ownFieldErrors(
  fields: readonly FormFieldDef[],
  errors: readonly AnswerError[],
): AnswerError[] {
  const known = knownFieldKeys(fields);
  return errors.filter((error) => known.has(error.key));
}

/**
 * 分组渲染视图：按 `groups.order` 排组、组内按 `fields.order` 排字段，
 * 只保留**当前可见**的字段，并丢掉因此变空的组。
 */
export function buildGroupedFields(schema: FormSchemaView, answers: Answers): FormFieldGroupView[] {
  const groups = [...schema.groups].sort((a, b) => a.order - b.order);
  const result: FormFieldGroupView[] = [];
  for (const group of groups) {
    const fields = schema.fields
      .filter((field) => field.group === group.key)
      .sort((a, b) => a.order - b.order)
      .filter((field) => isFieldVisible(field, answers));
    if (fields.length > 0) result.push({ key: group.key, label: group.label, fields });
  }
  return result;
}

/**
 * 分项报价与合计。
 *
 * 计费只认 `select` / `radio` 中**带 `priceCents` 的选项**（`PRICE_ELIGIBLE_FIELD_TYPES`），
 * 金额一律按分做整数运算，合计 = 分项之和 − 减免（不出现浮点）。
 */
export function computePriceBreakdown(
  fields: readonly FormFieldDef[],
  answers: Answers,
  reductionCents = 0,
): PriceBreakdown {
  const lines: PriceLine[] = [];
  for (const field of fields) {
    if (field.type !== FieldType.SELECT && field.type !== FieldType.RADIO) continue;
    if (!isFieldVisible(field, answers)) continue;
    const value = answers[field.key];
    if (typeof value !== 'string' || value === '') continue;
    const option = (field.options ?? []).find((item) => item.value === value);
    if (!option || typeof option.priceCents !== 'number') continue;
    lines.push({
      key: field.key,
      label: field.label,
      optionLabel: option.label,
      amountCents: option.priceCents,
    });
  }

  const itemsTotalCents = lines.reduce((sum, line) => sum + line.amountCents, 0);
  const safeReduction = Number.isInteger(reductionCents) && reductionCents > 0 ? reductionCents : 0;
  return {
    lines,
    itemsTotalCents,
    reductionCents: safeReduction,
    payableCents: itemsTotalCents - safeReduction,
  };
}

/** 取单个选项的报价文案（无报价返回 null，由调用方决定不展示） */
export function optionPriceText(option: { priceCents?: number }): string | null {
  return typeof option.priceCents === 'number' ? formatCentsText(option.priceCents) : null;
}

/**
 * 分 → `¥1,280.00` 文案。
 * 格式规则**复用** `@enroll/shared` 的 `formatCents`（三端同一份），
 * 这里只做「非整数分先取整」的防御，避免渲染期抛错把整页打挂。
 */
export function formatCentsText(cents: number): string {
  return formatCents(Number.isInteger(cents) ? cents : Math.round(cents));
}

/**
 * 整体校验。
 *
 * - `partial = false`（提交）：必填、格式、条件显示一次到位
 * - `partial = true`（草稿）：**不查必填**，但类型 / 选项 / 格式照拦
 *
 * 直接转交 `validateAnswers`，不额外加规则；隐藏字段由它内部跳过。
 */
export function collectErrors(
  fields: readonly FormFieldDef[],
  answers: Answers,
  options?: { partial?: boolean },
): AnswerError[] {
  return validateAnswers(fields, answers, options);
}

/** 单字段校验（`onBlur` / 步骤切换时用，避免每敲一个字就报错） */
export function collectFieldErrors(
  field: FormFieldDef,
  value: unknown,
  partial = false,
): AnswerError[] {
  return validateFieldValue(field, value, partial);
}

/** 错误数组 → 键到首条消息的映射（同一字段只展示第一条） */
export function firstErrorByKey(errors: readonly AnswerError[]): Record<string, string> {
  const map: Record<string, string> = {};
  for (const error of errors) {
    if (!(error.key in map)) map[error.key] = error.message;
  }
  return map;
}

/** 一期唯一能在线上走完核验链的证件类型（服务端 OCR 只认居民身份证） */
export const RESIDENT_ID_TYPE = 'resident_id';

/**
 * 只读分级。
 *
 * `graded = true` 表示「报名单已提交，字段按各自 `editable` 分级」：
 *   - `locked`：禁改（服务端会以 `ENROLL-007` 二次拦下）
 *   - `review`：**默认禁改**，只有进放行名单才可改（服务端 `ENROLL-008` 语义）
 *   - `free`：自由修改
 *
 * `graded = false`（草稿阶段）：全部可改，不提示。
 *
 * ⚠️ `review` 从「可改但提示复审」改成「未放行则禁改」（2026-10-27）：
 * 服务端对未放行的 `review` 字段一律 `409 ENROLL-008`，前端再让它能输入，
 * 等于让学员把整页填完、按保存、再被打回 —— 这才是真正的「提示需复审」在撒谎。
 * 放行名单来自后台的补交判定 / 驳回判定（`Registration.grantedFieldKeys`）。
 */
export function resolveFieldEditing(
  field: FormFieldDef,
  graded: boolean,
  grantedKeys: readonly string[] = [],
): FieldEditing {
  if (!graded) return { disabled: false, hint: null };
  if (field.editable === FieldEditable.LOCKED) {
    return { disabled: true, hint: '该字段已锁定，不可修改，如有误请联系客服' };
  }
  if (field.editable === FieldEditable.REVIEW) {
    if (grantedKeys.includes(field.key)) {
      // 放行本身就是复审结论，学员这一次改完就直接生效
      return { disabled: false, hint: '本次已获准修改，保存后即生效' };
    }
    return {
      disabled: true,
      hint: '该字段需客服核实后才能修改，请先处理页面提示的其它缺项',
    };
  }
  return { disabled: false, hint: null };
}

/** 字符数上限提示（textarea 展示用） */
export function maxLengthOf(field: FormFieldDef): number | null {
  const max = field.validation?.maxLength;
  return typeof max === 'number' ? max : null;
}
/** 可能承载「姓名」的 key（后台配的是 `name`，历史数据里也有用全称的） */
const NAME_FIELD_KEYS = new Set(['name', 'realName', 'applicantName']);

/** 可能承载「证件类型」的 key（后台配的是 `idType`） */
const ID_TYPE_FIELD_KEYS = new Set(['idType', 'certType', 'idCardType']);

/** 可能承载「性别」的 key */
const GENDER_FIELD_KEYS = new Set(['gender', 'sex']);

/** 可能承载「民族」的 key */
const ETHNICITY_FIELD_KEYS = new Set(['ethnicity', 'nation', 'ethnic']);

/**
 * 「性别」字段的判定。
 *
 * 为什么它要跟着身份证卡走（2026-10-21：用户裁定，见 `IdCardField` 文件头 ⑤）：
 * 证件上印着「男 / 女」，让人再去别的分组点一次纯属白干。判定方式与
 * `isIdTypeField` 一致：**先看 key，再退回看标签**，不写死一种 key。
 * 刻意**不**要求 `system === true` —— 后台把它配成普通自定义字段（seed 里 `system: false`）
 * 时也必须认得出来。
 */
export function isGenderField(field: FormFieldDef): boolean {
  return (
    (field.type === FieldType.RADIO || field.type === FieldType.SELECT) &&
    (GENDER_FIELD_KEYS.has(field.key) || field.label.replace(/\s/g, '') === '性别')
  );
}

/** 「民族」字段的判定：与性别同理，挂在身份证卡上由 OCR 预填 */
export function isEthnicityField(field: FormFieldDef): boolean {
  return (
    field.type === FieldType.TEXT &&
    (ETHNICITY_FIELD_KEYS.has(field.key) || field.label.replace(/\s/g, '') === '民族')
  );
}

/**
 * 「证件类型」字段的判定。
 *
 * 它天然属于身份证那一坨：类型决定「正面 / 反面该长什么样」，一旦和第 1 步里
 * 那些普通下拉摆在一起，学员就得先想清楚"我这是什么证件"，再去下面找拍照的地方。
 * 判定与 `isNameField` 同一套思路：先看 key，再退回看标签。
 */
export function isIdTypeField(field: FormFieldDef): boolean {
  return (
    field.type === FieldType.SELECT &&
    (ID_TYPE_FIELD_KEYS.has(field.key) || field.label.replace(/\s/g, '') === '证件类型')
  );
}

/** 「姓名」字段的判定（身份证复合卡与「身份核验」步骤共用同一套，别各写一遍） */
export function isNameField(field: FormFieldDef): boolean {
  return (
    field.type === FieldType.TEXT &&
    field.system === true &&
    (NAME_FIELD_KEYS.has(field.key) || field.label.replace(/\s/g, '') === '姓名')
  );
}

/**
 * 归拢「搬到身份核验步去填」的字段：身份证、手机号、与之配对的姓名、以及证件类型。
 *
 * 判定依据不是字段类型本身有多特殊，而是**它们在页面上都得靠一件"器械"才能产出**
 * —— 摄像头（身份证 / 人脸）与短信通道（手机号）。这一页集中了学员所有「动手」动作。
 *
 * ⚠️ 手写签名**不在**这里（2026-10-18 二次裁决）：它跟着「材料与承诺」那一组走，
 * 因为签名在业务上就是"对所交材料的承诺"，和学历证书、缴费凭证天然是一件事。
 *
 * 这些字段在 `FormRenderer` 里**不渲染**，但取值仍写进同一份表单答案 ——
 * 所以这里给出 key 列表，让页面知道要跳过哪些。
 */
export function resolveKycFields(fields: readonly FormFieldDef[]): {
  idField: FormFieldDef | null;
  idTypeField: FormFieldDef | null;
  genderField: FormFieldDef | null;
  ethnicityField: FormFieldDef | null;
  phoneField: FormFieldDef | null;
  nameField: FormFieldDef | null;
  keys: string[];
} {
  const idField = fields.find((field) => field.type === FieldType.IDCARD) ?? null;
  const phoneField = fields.find((field) => field.type === FieldType.PHONE) ?? null;
  const nameField = idField ? (fields.find((field) => isNameField(field)) ?? null) : null;
  // 证件类型不设 `type === IDCARD` 之类的硬前提：没有身份证字段时它也不该硬塞进核验页
  const idTypeField = idField ? (fields.find((field) => isIdTypeField(field)) ?? null) : null;
  // 性别 / 民族同理：**没有身份证就没有 OCR 预填**，那两行并过去也只会是一张孤零零的卡中卡
  const genderField = idField ? (fields.find((field) => isGenderField(field)) ?? null) : null;
  const ethnicityField = idField ? (fields.find((field) => isEthnicityField(field)) ?? null) : null;
  const keys = [
    idField?.key,
    idTypeField?.key,
    genderField?.key,
    ethnicityField?.key,
    phoneField?.key,
    nameField?.key,
  ].filter((key): key is string => typeof key === 'string');
  return { idField, idTypeField, genderField, ethnicityField, phoneField, nameField, keys };
}
